import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Title from "../../atoms/Title/Title";
import LogoSvg from "../../atoms/LogoSvg/LogoSvg";
import INFO from "../../../assets/images/INFO.svg";
import SubscriptionCard from "../../molecules/SubscriptionCard/SubscriptionCard";
import ModalSubscriptionInformations from "../../molecules/ModalSubscriptionInformations/ModalSubscriptionInformations";
import { OffersState } from "../../../types/rtk/subscription/subscription";
import { RootState } from "../../../types/rtk/api-config/types";
import { useAppDispatch, useAppSelector } from "../../../rtk/_config/hooks";
import Text from "../../atoms/Text/Text";
import { setBasket } from "../../../rtk/basket/basket";

interface Props {
  datas: OffersState;
  handleOpenGiftCard: () => void;
}

const SubscriptionChoice = ({ datas, handleOpenGiftCard }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const basket = useAppSelector((state: RootState) => state.basket);
  const giftcardSelected: boolean = !!basket.giftcard;

  const [isModalOpenInformation, setIsModalOpenInformation] = useState(false);
  const [showMoreOffers, setShowMoreOffers] = useState(false);

  const handleOpenInformation = () => setIsModalOpenInformation(true);
  const handleCloseInformation = () => setIsModalOpenInformation(false);

  const allOffers = datas.subscriptionsData;

  // Preselect offer with period=36 on mount if no offer is selected
  useEffect(() => {
    if (!basket.offer_id && allOffers.length > 0) {
      const defaultOffer = allOffers.find(offer => offer.period === 36);
      if (defaultOffer) {
        dispatch(setBasket({ ...basket, offer_id: defaultOffer.id }));
      }
    }
  }, [allOffers, basket, dispatch]);

  // Filter offers to hide certain periods unless selected or "show more" is active
  const hiddenOffersPeriods = [1];
  const visibleOffers = allOffers.filter(
    (item) =>
      item.id === basket.offer_id ||
      !hiddenOffersPeriods.includes(item.period) ||
      showMoreOffers
  ).sort((a, b) => a.period - b.period);
  const hasHiddenOffers = visibleOffers.length !== allOffers.length;

  return (
    <Box className="SubscriptionChoice">
      <Box className="subscriptionSubtitleContainer">
        <Title
          variant="h1"
          className="subscriptionSubtitle"
          children={t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.TITLE")}
        />
        <Box className="subscriptionInfoLogo">
          <LogoSvg src={INFO} onClick={handleOpenInformation} />
        </Box>
      </Box>

      <Box>
        {visibleOffers.map((obj, i) => {
          return (
            <SubscriptionCard
              key={i}
              index={i}
              data={obj}
              isActive={!giftcardSelected && basket.offer_id === obj.id}
              offers={allOffers}
            />
          );
        })}
        <SubscriptionCard
          isActive={giftcardSelected}
          onSelect={handleOpenGiftCard}
          offers={allOffers}
        />
        {hasHiddenOffers && (
          <Box className="toggleOffer" onClick={() => setShowMoreOffers((v) => !v)}>
            <Text children={t("subscription:FIRST_PAGE.SUBSCRIPTION_CHOICE.SEE_MORE")} />
          </Box>
        )}
      </Box>

      <ModalSubscriptionInformations
        isModalOpen={isModalOpenInformation}
        handleClose={handleCloseInformation}
      />
    </Box>
  );
};

export default SubscriptionChoice;
